import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { catchError, EMPTY, filter, take } from 'rxjs';
import type { UserTenant } from '../../../core/models/user-tenant.model';
import { TenantSwitchService } from '../../../core/services/tenant-switch.service';
import { UserSessionService } from '../../../core/services/user-session.service';
import { LayoutService } from '../../../core/services/layout.service';
import { ThemeToggle } from '../theme-toggle/theme-toggle';
import { LanguageSwitcher } from '../language-switcher/language-switcher';
import { shellTitleKey } from '../../../layout/site.routing';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { SidebarModulesService } from '../../../core/services/sidebar-modules.service';
import { isModuleRootUrl } from '../../../core/navigation/sidebar-module-catalog';
import { normalizeSidebarRole, SidebarAppRole } from '../sidebar/sidebar-nav.util';

function shellTitleFromSnapshot(root: ActivatedRouteSnapshot): string {
  let bestDepth = -1;
  let title = 'nav.dashboard';
  const walk = (s: ActivatedRouteSnapshot, depth: number) => {
    const raw = s.data[shellTitleKey];
    if (typeof raw === 'string' && raw.trim().length > 0 && depth >= bestDepth) {
      bestDepth = depth;
      title = raw.trim();
    }
    for (const c of s.children) {
      walk(c, depth + 1);
    }
  };
  walk(root, 0);
  return title;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.html',
  styleUrl: './header.scss',
  imports: [
    ThemeToggle,
    LanguageSwitcher,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    FormsModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header {
  protected readonly layout = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly session = inject(UserSessionService);
  private readonly tenantSwitch = inject(TenantSwitchService);
  private readonly sidebarModules = inject(SidebarModulesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly isSwitchingTenant = this.tenantSwitch.isSwitching;

  readonly isSuperAdmin = computed(
    () => normalizeSidebarRole(this.session.user()) === SidebarAppRole.SuperAdmin,
  );

  readonly routeTitle = signal('nav.dashboard');
  private readonly currentUrl = signal(this.router.url);

  /** Localized module title for module-root URLs; detail pages keep route title keys. */
  readonly moduleLabelKey = computed(() => {
    const url = this.currentUrl();
    if (!isModuleRootUrl(url)) {
      return null;
    }
    return this.sidebarModules.labelKeyForUrl(url);
  });
  /** Elevated shadow when the page is scrolled (window scroll). */
  readonly headerElevated = signal(false);

  readonly organizationOptions = computed<UserTenant[]>(() => {
    const user = this.session.user();
    const storedTenants = this.session.tenants();
    const options: UserTenant[] = [];
    const seen = new Set<string>();

    if (user?.tenantId) {
      const tenantId = user.tenantId.trim();
      const qrToken = (user.qrToken ?? '').trim();
      const logo = user.logo?.trim();

      if (tenantId && qrToken) {
        options.push({
          tenantId,
          tenantName: user.tenantName?.trim() || 'Organization',
          logo: logo ?? '',
          qrToken,
        });
        seen.add(tenantId);
      }
    }

    for (const tenant of storedTenants) {
      if (seen.has(tenant.tenantId)) {
        continue;
      }

      options.push(tenant);
      seen.add(tenant.tenantId);
    }

    return options;
  });

  private readonly failedLogos = new Set<string>();

  readonly selectedOrganization = linkedSignal<UserTenant | null, UserTenant | null>({
    source: () => {
      const user = this.session.user();
      const options = this.organizationOptions();
      if (!user?.tenantId) {
        return options[0] ?? null;
      }

      return options.find((option) => option.tenantId === user.tenantId) ?? options[0] ?? null;
    },
    computation: (source) => source,
  });

  constructor() {
    const syncTitle = () => {
      this.routeTitle.set(shellTitleFromSnapshot(this.router.routerState.snapshot.root));
    };
    syncTitle();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
        syncTitle();
      });
    queueMicrotask(() => this.onWindowScroll());
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const y = window.scrollY ?? document.documentElement.scrollTop ?? 0;
    const next = y > 6;
    if (this.headerElevated() !== next) {
      this.headerElevated.set(next);
    }
  }

  onOrganizationChange(org: UserTenant | null): void {
    const tenantId = org?.tenantId?.trim();
    if (!tenantId) {
      return;
    }

    const currentTenantId = this.session.user()?.tenantId?.trim();
    if (tenantId === currentTenantId) {
      this.selectedOrganization.set(org);
      return;
    }

    const previous = this.selectedOrganization();
    this.selectedOrganization.set(org);

    this.tenantSwitch
      .switchTenant(tenantId)
      .pipe(
        take(1),
        catchError(() => {
          this.selectedOrganization.set(previous);
          return EMPTY;
        }),
      )
      .subscribe({
        next: () => {
          window.location.reload();
        },
      });
  }

  showOrganizationLogo(org: UserTenant | null | undefined): boolean {
    const url = (org?.logo ?? '').trim();
    return url.length > 0 && !this.failedLogos.has(url);
  }

  organizationLogo(org: UserTenant | null | undefined): string {
    return (org?.logo ?? '').trim();
  }

  onOrganizationLogoError(org: UserTenant | null | undefined): void {
    const url = this.organizationLogo(org);
    if (!url || this.failedLogos.has(url)) {
      return;
    }

    this.failedLogos.add(url);
    this.cdr.markForCheck();
  }

  onMenuClick(): void {
    if (typeof matchMedia === 'undefined') {
      return;
    }
    if (matchMedia('(max-width: 991px)').matches) {
      this.layout.toggleMobileSidebar();
    } else {
      this.layout.toggleDesktopSidebar();
    }
  }

  // Logout is in the sidebar user card in this shell.
}
